/**
 * Theme Requirements Aggregation for Weidner Apartment Homes
 * 
 * Extends the base avenue theme requirements with Weidner Apartment Homes specific
 * component requirements.
 */

import type { ComponentRequirements } from '@vibesites/tokens';
import { defineComponentRequirements } from '@vibesites/tokens';
import { componentRequirements as baseRequirements } from '@vibesites/theme-avenue/requirements';

/**
 * Weidner Apartment Homes component requirement overrides.
 *
 * Header and Footer drop `property_google_place_url` from their fields
 * arrays — the address link URL is now resolved at render time via
 * `usePropertyAddressUrl()`, which internally falls back through
 * property_google_place_url -> property_google_business_url ->
 * property_full_address -> lat/lon. `property_full_address` is already
 * required, so no replacement field is needed here.
 */
const weidnerapartmenthomesOverrides: Record<string, ComponentRequirements> = {
  Header: defineComponentRequirements({
    tokens: ['color.primary', 'color.secondary', 'color.accent', 'color.text', 'color.textMuted', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    fields: ['property_phone', 'property_full_address', 'property_mono_black_logo', 'property_secondary_logo', 'property_primary_logo', 'property_apply_now_url', 'property_resident_portal_url'],
  }),
  Footer: defineComponentRequirements({
    tokens: ['color.text', 'color.border', 'font.heading', 'font.body'],
    fields: ['property_phone', 'property_address_1', 'property_address_city', 'property_address_state', 'property_address_zip', 'property_mono_white_logo', 'property_mono_black_logo', 'property_company_mono_white_logo', 'property_company_url', 'property_facebook_url', 'property_instagram_url', 'property_youtube_url'],
    collections: ['office_hours'],
  }),
};

/**
 * All component requirements for the Weidner Apartment Homes theme.
 * Inherits from avenue theme and overrides with Weidner Apartment Homes components.
 */
export const componentRequirements: Record<string, ComponentRequirements> = {
  ...baseRequirements,
  ...weidnerapartmenthomesOverrides,
};

/**
 * Get requirements for a specific component.
 */
export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

/**
 * Get all component names that have requirements defined.
 */
export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
